import {
  getSkillReportByProgramCourseRefScope,
  getStudentPerformanceReport
} from "../../../../services/ready-services/report/reportQueryResolver"
import {
  ProgramAndCoursesInfo,
  SkillReportResponse,
  refScope
} from "../../../../types/gqlTypes/graphql"
import { PdfGeneratorGenerateOptions } from "../../../mp-shared-components/generic-shared-components/components"
import { StudentPerformanceDataBySubject } from "../../components/StudentPerformanceReportPdfMarkup/types"
import { SUBTESTS_EXCLUDED_FROM_OVERALL_ARC_BY_PROGRAM } from "./constants"

const getFallbackCourseCategory = (courseName: string): string => {
  const normalizedCourseName = courseName.toLowerCase()

  if (normalizedCourseName.includes("science")) {
    return "science"
  }
  if (normalizedCourseName.includes("writing")) {
    return "writing"
  }
  if (normalizedCourseName.includes("reading")) {
    return "reading"
  }
  if (normalizedCourseName.includes("math")) {
    return "math"
  }
  if (normalizedCourseName.includes("english")) {
    return "english"
  }

  return "overall"
}

const getDisplayScore = (
  score: number | null | undefined,
  minScore: number | null | undefined
): number | null => {
  if (score == null) {
    return null
  }

  if (minScore != null && minScore > 0 && score < minScore) {
    return null
  }

  return score
}

export const getStudentPerformanceReportData = async (args: {
  contentId: string
  studentRefScope: refScope
  programsAndCourses: ProgramAndCoursesInfo[]
}): Promise<{
  program: string
  programDisplayName: string
  testName: string
  studentName: string
  isOverallAvailable: boolean
  overallScore: number
  overallMinScore: number
  overallMaxScore: number
  subtestsData: StudentPerformanceDataBySubject
} | null> => {
  const { contentId, studentRefScope, programsAndCourses } = args

  const reports = await getStudentPerformanceReport(
    [contentId],
    studentRefScope
  )

  const report = reports?.reports?.[0]?.reports?.[0]

  if (!report) {
    return null
  }

  const program = reports.reports[0].program
  const programData = programsAndCourses?.find(item => item.program === program)
  const programCourses = programData?.courses || []

  const testName = report.testName
  const studentName = report.studentName

  const isOverallAvailable = !!report.overallScore?.isAvailable
  const overallScore = isOverallAvailable
    ? (report.overallScore?.score ?? null)
    : null

  const subtestsExcludedFromOverallArc =
    SUBTESTS_EXCLUDED_FROM_OVERALL_ARC_BY_PROGRAM[program] || []
  const reportSubtests = report.subtests || []
  const visibleCourseNames = new Set(programCourses.map(course => course.name))
  const skillReportSubtests = reportSubtests.filter(
    item => visibleCourseNames.has(item.subtestName)
  )

  const skillReportBySubject = await Promise.all(
    skillReportSubtests.map(item =>
      getSkillReportByProgramCourseRefScope({
        program,
        subject: item.subtestName,
        refScope: studentRefScope,
        contentIds: [contentId]
      })
    )
  ).then(response =>
    skillReportSubtests.reduce(
      (res: Record<string, SkillReportResponse>, item, index) => ({
        ...res,
        [item.subtestName]: response?.[index] || null
      }),
      {}
    )
  )

  let overallMinScore = 0
  let overallMaxScore = 0

  const programCourseByName = new Map(
    programCourses.map(course => [course.name, course])
  )
  const subtestNames = [
    ...programCourses.map(course => course.name),
    ...reportSubtests.map(item => item.subtestName)
  ].filter((subtestName, index, allSubtestNames) => {
    return allSubtestNames.indexOf(subtestName) === index
  })

  const subtestsData: StudentPerformanceDataBySubject = subtestNames?.reduce(
    (res, course) => {
      const reportData =
        reportSubtests.find(item => item.subtestName === course) ||
        null

      const skillReport = skillReportBySubject[course] || null

      const questionsAttempted = !!reportData
        ? reportData.items?.filter(item => !!item.answer)?.length || null
        : null

      const excludedFromOverallScoreArc =
        subtestsExcludedFromOverallArc.includes(course)

      const category =
        programCourseByName.get(course)?.courseCategories?.at(-1) ||
        getFallbackCourseCategory(course)

      if (!reportData) {
        return {
          ...res,
          [course]: {
            subtestContentId: "",
            subtestName: course,
            status: "NOT_STARTED",
            score: null,
            questionsCount: 0,
            correctQuestionsCount: 0,
            completedDate: null,
            accuracyPercent: 0,
            maxScore: null,
            minScore: null,
            items: [],
            subScores: [],
            questionsAttempted: null,
            skillReport: null,
            excludedFromOverallScoreArc,
            hasReportData: false,
            category
          }
        }
      }

      if (!excludedFromOverallScoreArc) {
        overallMinScore = Math.min(overallMinScore, reportData?.minScore ?? 0)
        overallMaxScore = overallMaxScore + (reportData?.maxScore ?? 0)
      }

      const score = getDisplayScore(reportData.score, reportData.minScore)

      return {
        ...res,
        [course]: {
          ...(reportData || {}),
          score,
          questionsAttempted,
          skillReport,
          excludedFromOverallScoreArc,
          hasReportData: true,
          category
        }
      }
    },
    {}
  )

  return {
    program,
    programDisplayName: programData?.programInfo?.displayName || program,
    testName,
    studentName,
    isOverallAvailable,
    overallScore,
    overallMinScore,
    overallMaxScore,
    subtestsData
  }
}

export const getGeneratePdfOptions = async (args: {
  testName: string
  studentName: string
  programDisplayName: string
  subtestsData: StudentPerformanceDataBySubject
  isOverallAvailable: boolean
  overallScore: number
  overallMinScore: number
  overallMaxScore: number
}): Promise<PdfGeneratorGenerateOptions> => {
  const {
    testName,
    studentName,
    programDisplayName,
    subtestsData,
    isOverallAvailable,
    overallScore,
    overallMinScore,
    overallMaxScore
  } = args

  const {
    StudentPerformanceReportContentMarkup,
    StudentPerformanceReportHeaderMarkup
  } = await import("../../components/StudentPerformanceReportPdfMarkup")

  return {
    fileName: `${testName} ${studentName ?? "Student"} Performance Report.pdf`,
    documentTitle: testName,
    page: { margin: "0in" },
    header: {
      content: (
        <StudentPerformanceReportHeaderMarkup
          testName={testName}
          studentName={studentName}
        />
      ),
      marginSize: "0.6in"
    },
    content: (
      <StudentPerformanceReportContentMarkup
        program={programDisplayName}
        subtestsData={subtestsData}
        isOverallAvailable={isOverallAvailable}
        overallScore={overallScore}
        overallMinScore={overallMinScore}
        overallMaxScore={overallMaxScore}
      />
    )
  }
}
